import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  TimeTrackingEmployeeLineItemJson,
  TimeTrackingServiceCreateResponseJson,
  TimeTrackingServiceRowJson,
} from '@vmspdb/definitions';
import { Paginated } from '@vmspdb/ui/domain';
import * as fromPaginatedTimeTracking from './paginated-time-tracking.reducer';
// import { selectAllProjectsMapped } from '../projects';
// import { selectAllClientsMapped } from '../clients';
// import {
//   ClientServiceCreateResponse,
//   TimeTrackingServiceCreateResponse,
// } from '@vmspdb/definitions';

export const selectPaginatedTimeTrackingState =
  createFeatureSelector<fromPaginatedTimeTracking.PaginatedTimeTrackingState>(
    fromPaginatedTimeTracking.paginatedTimeTrackingFeatureKey,
  );

export const selectPaginatedTimeTrackingPagination = createSelector(
  selectPaginatedTimeTrackingState,
  (state) => {
    const { page, total } = state;
    return { page, total };
  },
);

export const selectPaginatedTimeTrackings = createSelector(
  selectPaginatedTimeTrackingState,
  (state) => state.items,
);

export const selectPaginatedTimeTrackingsForTable = createSelector(
  selectPaginatedTimeTrackingState,
  (state): Paginated<TimeTrackingServiceCreateResponseJson> => state,
);

export const selectPaginatedTimeTrackingError = createSelector(
  selectPaginatedTimeTrackingState,
  (state) => state.error,
);

export const selectPaginatedTimeTrackingsGroupedByEmployee = createSelector(
  selectPaginatedTimeTrackings,
  (trackings): Paginated<TimeTrackingEmployeeLineItemJson> => {
    const employeeMappings = new Map<string, number>();

    trackings.forEach((tracking) => {
      const employeeUuid = tracking.employeeUuid || '';
      const totalHours = Number(tracking.totalHours ?? 0);
      employeeMappings.set(
        employeeUuid,
        totalHours + (employeeMappings.get(employeeUuid) ?? 0),
      );
    });

    const results = [...employeeMappings].map(
      ([employeeUuid, totalHours]) =>
        ({
          employeeUuid,
          totalHours,
        }) satisfies TimeTrackingEmployeeLineItemJson,
    );

    return {
      page: 0,
      total: results.length,
      items: results,
    };
  },
);

enum Grouping {
  ALL,
  EMPLOYEE,
  PROJECT,
}

export const selectPaginatedAllTimeTrackingRows = createSelector(
  selectPaginatedTimeTrackings,
  selectPaginatedTimeTrackingPagination,
  (records, pagination): Paginated<TimeTrackingServiceRowJson> => {
    return mapRecords(records, pagination, Grouping.ALL);
  },
);

export const selectPaginatedProjectTimeTrackingRows = createSelector(
  selectPaginatedTimeTrackings,
  selectPaginatedTimeTrackingPagination,
  (records, pagination): Paginated<TimeTrackingServiceRowJson> => {
    return mapRecords(records, pagination, Grouping.PROJECT);
  },
);

export const selectPaginatedEmployeeTimeTrackingRows = createSelector(
  selectPaginatedTimeTrackings,
  selectPaginatedTimeTrackingPagination,
  (records, pagination): Paginated<TimeTrackingServiceRowJson> => {
    return mapRecords(records, pagination, Grouping.EMPLOYEE);
  },
);

function isCustomSurveyTask(task: unknown): boolean {
  if (!task || typeof task !== 'object') {
    return false;
  }

  const typedTask = task as {
    type?: { case?: string };
    custom?: unknown;
  };

  return typedTask.type?.case === 'custom' || Boolean(typedTask.custom);
}

function mapRecords(
  records: TimeTrackingServiceCreateResponseJson[],
  pagination: {
    page: number;
    total: number;
  },
  grouping: Grouping,
): Paginated<TimeTrackingServiceRowJson> {
  const buckets = new Map<string, TimeTrackingServiceCreateResponseJson[]>();

  records.forEach((record) => {
    let key = '';
    switch (grouping) {
      case Grouping.PROJECT:
        key = record.projectUuid || '';
        break;
      case Grouping.EMPLOYEE:
        key = record.employeeUuid || '';
        break;
    }

    buckets.set(key, [...(buckets.get(key) ?? []), record]);
  });

  // buckets = new Map(
  //   [...buckets.entries()].sort((a, b) => {
  //     const aDate = JSON.parse(a[0]).date;
  //     const bDate = JSON.parse(b[0]).date;
  //
  //     if (aDate === bDate) {
  //       return 0;
  //     }
  //
  //     return aDate < bDate ? -1 : 1;
  //   }),
  // );

  const result: TimeTrackingServiceRowJson[] = [];

  buckets.forEach((items) => {
    const totalHours = items.reduce(
      (prev, curr) => prev + Number(curr.totalHours ?? 0),
      0,
    );
    const totalSeconds = items.reduce(
      (prev, curr) =>
        prev +
        (Number(curr.totalSeconds ?? 0) > 0
          ? Number(curr.totalSeconds ?? 0)
          : Number(curr.totalHours ?? 0) * 3600),
      0,
    );
    const totalBilledHours = items.reduce((prev, curr) => {
      let billedHours = 0;

      // Only custom survey tasks are billable
      if (
        curr.projectSurveyTaskUuid &&
        curr.denormalized?.surveyTask?.tasks?.some(isCustomSurveyTask)
      ) {
        billedHours = Number(curr.billing?.billedHours ?? 0);
      }
      return prev + billedHours;
    }, 0);
    items.forEach((value) => result.push({ record: value }));
    result.push({
      heading: {
        totalHours,
        totalSeconds,
        totalBilledHours,
      },
    });
  });

  return {
    ...pagination,
    items: result,
  };
}
